import React, { useEffect, useRef } from 'react';
import usePoiUpdateHandler from '../../components/pointsOfInterest/usePoiUpdateHandler';
import { Label } from '../../../common/labels/labelTypes';
import { Tag as LabelPill, TagGroup as LabelGroup, TagList as LabelList } from '@milanote/roma';
import { List } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedElementId, getSelectedLabelIdForElement } from './labelSelectors';
import { deselectAllElements } from '../selection/selectionActions';
import { deselectLabels, selectLabels } from '../../../common/labels/labelActions';
import { removeLabelFromElements } from './elementLabelActions';
import { isFeatureEnabled } from '../../../common/users/utils/userPropertyUtils';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';
import { getCurrentUser } from '../../user/currentUserSelector';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { cx } from '@milanote/roma/utils/cx';
import { canWrite } from '../../../common/permissions/permissionUtil';
import { markEventAsHandled } from '../../utils/react/reactEventUtil';

type LabelElementPillsContainerProps = {
    labels: List<Label>;
    element: ImMNElement;
    size: 'xs' | 'sm' | 'md' | 'lg' | undefined;
    permissions: number;
    filterQuery?: string;
};

const SEARCH_HIGHLIGHT_CLASS = 'search-highlight';

const LabelElementPillsContainer: React.FC<LabelElementPillsContainerProps> = ({
    labels,
    filterQuery,
    size,
    element,
    permissions,
}) => {
    const dispatch = useDispatch();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const elementId = getElementId(element);
    const selectedLabelId = useSelector(getSelectedLabelIdForElement(elementId));
    const selectedElementId = useSelector(getSelectedElementId);
    const currentUser = useSelector(getCurrentUser);

    const selectedKeys = selectedLabelId ? new Set<string>([selectedLabelId]) : new Set<string>();
    const canSelectLabels = canWrite(permissions);

    const handleSelectedKeyChange = (keys: Set<string>) => {
        if (!canSelectLabels || !isFeatureEnabled(ExperimentId.tags, currentUser)) return;

        dispatch(deselectAllElements());
        dispatch(selectLabels(Array.from(keys)[0], elementId));
    };

    const onHighlightsChanged = usePoiUpdateHandler({
        contentInnerRef: containerRef,
        elementId,
        selector: `.${SEARCH_HIGHLIGHT_CLASS}`,
    });

    useEffect(() => {
        if (!containerRef.current) return;

        const handleOutsideClick = (event: MouseEvent) => {
            if (selectedElementId !== elementId) return;

            const target = event.target as Node | null;
            if (!target || containerRef.current?.contains(target)) return;

            const elementTarget = target instanceof Element ? target : target.parentElement;
            if (!elementTarget?.closest('.canvas-section') || elementTarget?.closest('.LabelElementPillsContainer'))
                return;

            dispatch(deselectLabels());
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (selectedElementId !== elementId) return;
            if (selectedKeys.size === 0) return;
            if (!canSelectLabels) return;

            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                const selectedLabelIdToRemove = Array.from(selectedKeys)[0];
                dispatch(removeLabelFromElements(List([element]), selectedLabelIdToRemove));
                dispatch(deselectLabels());
            }
        };

        if (selectedLabelId) {
            document.addEventListener('click', handleOutsideClick, true);
            document.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            document.removeEventListener('click', handleOutsideClick, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [dispatch, selectedLabelId, selectedElementId, elementId, labels, canSelectLabels, selectedKeys, element]);

    useEffect(() => {
        if (!canSelectLabels && selectedLabelId) {
            dispatch(deselectLabels());
        }
    }, [canSelectLabels, dispatch, selectedLabelId]);

    useEffect(() => {
        onHighlightsChanged();
    }, [filterQuery]);

    if (!labels?.size) return null;

    /**
     * Prevent read-only label interactions from bubbling into the element
     * selection flow if the user can't select labels.
     */
    const preventElementSelectionFromReadOnlyLabelInteraction = (event: React.MouseEvent<HTMLDivElement>) => {
        if (canSelectLabels) return;

        markEventAsHandled(event);
    };

    return (
        <div
            className="LabelElementPillsContainer mb-1 mr-1"
            ref={containerRef}
            onClick={preventElementSelectionFromReadOnlyLabelInteraction}
            onMouseDown={preventElementSelectionFromReadOnlyLabelInteraction}
        >
            <LabelGroup
                label="Labels list mb-1"
                size={size}
                selectionMode={canSelectLabels ? 'single' : 'none'}
                selectedKeys={selectedKeys}
                onSelectionChange={(keys) => handleSelectedKeyChange(keys as Set<string>)}
            >
                <LabelList className="flex flex-wrap gap-1">
                    {labels.map((label) => {
                        if (!label) return null;

                        const isSearchResult =
                            filterQuery && label.name.toLowerCase().includes(filterQuery.toLowerCase());

                        const tagBaseClassName = cx(
                            'transition-none',
                            isSearchResult && `${SEARCH_HIGHLIGHT_CLASS} ring-2 ring-search`,
                        );

                        return (
                            <LabelPill
                                label={label.name}
                                key={label.id}
                                id={label.id}
                                color={label.color}
                                tagBaseClassName={tagBaseClassName}
                            >
                                {label.name}
                            </LabelPill>
                        );
                    })}
                </LabelList>
            </LabelGroup>
        </div>
    );
};

export default LabelElementPillsContainer;
